import { CCFPackagingGroup } from '@shared/api/types';
import {
  CategorizedPicker,
  Category,
} from '@shared/components/form/categorized-picker';
import SelectFormField, {
  SelectOption,
} from '@shared/components/form/select-form-field';
import { FormProps } from '@shared/components/form/types';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@shared/components/ui/form';
import { Skeleton } from '@shared/components/ui/skeleton';
import { cn } from '@shared/lib/utils';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

interface PackagingPickerProps extends FormProps {
  availablePackaging: CCFPackagingGroup[];
  availableSizes: SelectOption[];
  sizeName: string;
  isLoading?: boolean;
}

export default function PackagingPicker({
  name,
  sizeName,
  label,
  className,
  includeErrorMessage,
  availablePackaging,
  availableSizes,
  isLoading,
}: PackagingPickerProps) {
  const { control, setValue } = useFormContext();

  const categories: Category<CCFPackagingGroup>[] = useMemo(() => {
    const defaultPackagingCategory: Category<CCFPackagingGroup> = {
      name: 'Standard packaging',
      items: availablePackaging,
      searchableKeys: ['name'],
      valueKey: 'name',
      labelKey: 'name',
      onSelect: (selectedItem: CCFPackagingGroup) => {
        setValue(name, selectedItem.name);
        // Clear the size selection when packaging type changes
        setValue(sizeName, '');
      },
    };

    return [defaultPackagingCategory] as Category<CCFPackagingGroup>[];
  }, [availablePackaging, name, setValue, sizeName]);

  return (
    <div className="grid grid-cols-12 gap-3">
      <FormField
        control={control}
        name={name}
        render={({ field, fieldState }) => {
          return (
            <FormItem className={cn('grid flex-1 items-center', className)}>
              {label && <FormLabel>Type</FormLabel>}
              <FormControl>
                <>
                  {isLoading && <Skeleton className="h-10 w-full rounded-md" />}
                  {field.value && !isLoading && (
                    <CategorizedPicker
                      categories={categories}
                      value={field.value}
                      key={categories[0].items.length}
                      error={fieldState.error?.message}
                    />
                  )}
                </>
              </FormControl>
              {includeErrorMessage && <FormMessage />}
            </FormItem>
          );
        }}
      />
      <SelectFormField
        name={sizeName}
        className={cn('', className)}
        options={availableSizes}
        label="Size"
        includeErrorMessage
        loadingOptions={isLoading}
      />
    </div>
  );
}

export function useAvailableSizes(
  packagingOptions: CCFPackagingGroup[],
  watchSelectedPackagingOptionName: string
): SelectOption[] {
  return useMemo(() => {
    if (!watchSelectedPackagingOptionName || !packagingOptions) {
      return [];
    }

    const selectedPackaging = packagingOptions.find(
      (packaging) => packaging.name === watchSelectedPackagingOptionName
    );

    if (!selectedPackaging) {
      return [];
    }
    return selectedPackaging?.sizes.map((size) => ({
      label: `${size.size}${size.sizeDescription && `, ${size.sizeDescription.toLowerCase()}`}`,
      value: size.uuid,
    }));
  }, [packagingOptions, watchSelectedPackagingOptionName]);
}
